import React, { createContext, useState, useContext } from 'react';
import { DataLoadingScreen } from '../components/common';

const LoadingContext = createContext(null);

export const LoadingProvider = ({ children }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [loadingText, setLoadingText] = useState('Caricamento...');
  const [progress, setProgress] = useState(0);
  const [showPercentage, setShowPercentage] = useState(true);

  const activeFilesRef = React.useRef(0);
  const manualLockRef = React.useRef(false);
  const expectedStepsRef = React.useRef(1);

  // Global Interceptor for clientDataService fetches
  React.useEffect(() => {
    let activeFiles = {};
    let completedFiles = 0;
    let totalHistoricalFiles = 0;
    let hideTimer = null;

    const updateState = () => {
      const paths = Object.keys(activeFiles);
      const count = paths.length;
      activeFilesRef.current = count;
      
      const totalSessionFiles = completedFiles + count;
      if (totalSessionFiles > totalHistoricalFiles) {
        totalHistoricalFiles = totalSessionFiles;
      }
      
      const targetTotal = Math.max(expectedStepsRef.current, totalHistoricalFiles);

      if (count === 0) {
        if (!manualLockRef.current) {
          setProgress(100);
          hideTimer = setTimeout(() => {
            if (!manualLockRef.current) {
              setIsLoading(false);
              setProgress(0);
              completedFiles = 0;
              totalHistoricalFiles = 0;
              expectedStepsRef.current = 1;
            }
          }, 500); 
        } else {
          // If manually locked, update the frozen progress bar accurately
          let baseCalc = Math.round(((completedFiles * 100) / targetTotal));
          setProgress(Math.min(baseCalc, 99)); // Cap at 99% to indicate CPU processing is still ongoing if locked!
        }
      } else {
        if (hideTimer) clearTimeout(hideTimer);
        setIsLoading(true);

        setLoadingText('Recupero dei dati in corso...');

        let activePctSum = 0;
        paths.forEach(p => activePctSum += activeFiles[p]);
        
        let overallProgress = Math.round(((completedFiles * 100) + activePctSum) / targetTotal);
        if (manualLockRef.current) {
          overallProgress = Math.min(overallProgress, 99); // Never show 100% until hideLoader triggers
        }
        setProgress(overallProgress);
      }
    };

    const handleStart = (e) => {
      const path = e.detail.filePath || `dati_${Date.now()}`;
      activeFiles[path] = 0;
      updateState();
    };

    const handleProgress = (e) => {
      const path = e.detail.filePath;
      if (path && activeFiles[path] !== undefined) {
        activeFiles[path] = e.detail.percentage;
        updateState();
      }
    };

    const handleEnd = (e) => {
      const path = e.detail.filePath;
      if (path && activeFiles[path] !== undefined) {
        delete activeFiles[path];
        completedFiles++;
        updateState();
      }
    };

    window.addEventListener('global-loading-start', handleStart);
    window.addEventListener('global-loading-progress', handleProgress);
    window.addEventListener('global-loading-end', handleEnd);

    return () => {
      window.removeEventListener('global-loading-start', handleStart);
      window.removeEventListener('global-loading-progress', handleProgress);
      window.removeEventListener('global-loading-end', handleEnd);
      if (hideTimer) clearTimeout(hideTimer);
    };
  }, []);

  const showLoader = (text = 'Recupero dei dati in corso...', expectedSteps = 1, options = { showPercentage: true }) => {
    setLoadingText(text);
    setShowPercentage(options.showPercentage !== false);
    manualLockRef.current = true;
    expectedStepsRef.current = expectedSteps;
    setIsLoading(true);
  };

  const updateProgress = (percentage) => {
    setProgress(percentage);
  };

  const hideLoader = () => {
    manualLockRef.current = false;
    setShowPercentage(true); // Reset to default for next call
    
    // Explicitly surge to 100% right before disappearing to show completion of locked CPU processes
    setProgress(100);
    
    // Adding a slight delay to hide so users can register '100%' if reached
    setTimeout(() => {
      // Don't close if background files are still actively fetching!
      if (activeFilesRef.current === 0) {
        setIsLoading(false);
        setProgress(0);
      }
    }, 400); // Wait slightly longer for smooth visual transition
  };

  // Helper template for simulating progress for requests that don't have native progress tracking
  const simulateProgress = async (promise, text = 'Elaborazione...', options = { showPercentage: true }) => {
    showLoader(text, 1, options);
    let intervalProgress = 0;

    // Increment progress by 10% every 200ms up to 90%
    const interval = setInterval(() => {
      intervalProgress = Math.min(intervalProgress + 10, 90);
      updateProgress(intervalProgress);
    }, 200);

    try {
      const result = await promise;
      // When the promise resolves, bump to 100% and hide
      clearInterval(interval);
      updateProgress(100);
      hideLoader();
      return result;
    } catch (error) {
      clearInterval(interval);
      hideLoader();
      throw error;
    }
  };

  return (
    <LoadingContext.Provider value={{ isLoading, showLoader, hideLoader, updateProgress, simulateProgress }}>
      {isLoading && (
        <DataLoadingScreen
          visible={isLoading}
          percentage={showPercentage ? progress : null}
          text={loadingText}
        />
      )}
      {children}
    </LoadingContext.Provider>
  );
};

export const useLoading = () => useContext(LoadingContext);
